import { reloadPage } from './leave';

/**
 * A tab opened before a console deploy holds the old index.html, whose
 * page chunks have hashed names the new deploy no longer serves (Workers
 * static assets keep the current version only). The first time such a tab
 * asks for a page it has not loaded yet, the import fails and the page
 * would break. Instead the tab loads itself again, once, and the new
 * index.html names the new chunks. PreloadPages (App.tsx) narrows the
 * window; this closes it.
 *
 * Only an import whose failure leaves nothing to show reloads: a page
 * (App.tsx) and the customiser's CSS editor (customiser/groups/Brand.tsx),
 * both through `reloadingImport`. Imports a page makes for an action, such
 * as prettier's format and the redirects CSV parser, report their own
 * failure where they are, as before: a reload there would throw away an
 * edit not yet written.
 *
 * Two routes in, one decision: Vite's preload helper dispatches
 * `vite:preloadError` as an import fails (and rethrows, since nothing
 * here cancels it), and the import's own rejection reaches
 * `reloadingImport`. Either reloads, at most once: a sessionStorage stamp
 * refuses a second reload within RELOAD_WINDOW_MS, so a deploy whose
 * chunks are truly missing shows its error rather than reloading forever,
 * while a tab that lives on past a later deploy can reload again then.
 * No sessionStorage (blocked storage): no reload, since nothing could
 * stop a loop.
 */
const RELOAD_KEY = 'console:stale-chunk-reload';
export const RELOAD_WINDOW_MS = 60_000;

let reloading = false;
let renderingLoads = 0;

/** True when a reload is on its way (this call or an earlier one); false when the guard refuses it. */
export function reloadOnce(now = Date.now()): boolean {
  if (reloading) return true;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY));
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  reloading = true;
  reloadPage();
  return true;
}

/**
 * Wraps a `lazy()` loader whose failure leaves nothing to show: a failed
 * load reloads the page once and never settles (the Suspense fallback, or
 * the last page, stays up until the reload); refused, it rejects with the
 * import's own error, as an unwrapped import would.
 */
export function reloadingImport<T>(load: () => Promise<T>): () => Promise<T> {
  return () => {
    renderingLoads += 1;
    return load().then(
      (mod) => {
        renderingLoads -= 1;
        return mod;
      },
      (err: unknown) => {
        renderingLoads -= 1;
        if (reloadOnce()) return new Promise<T>(() => {});
        throw err;
      },
    );
  };
}

/** `vite:preloadError`: reloads only while a `reloadingImport` load is in flight (see above). */
export function onPreloadError(): void {
  if (renderingLoads > 0) reloadOnce();
}

/** Called once, by main.tsx. */
export function installStaleChunkReload(target: Window = window): void {
  target.addEventListener('vite:preloadError', onPreloadError);
}

/** Tests only: a fresh document. */
export function resetStaleChunkForTest(): void {
  reloading = false;
  renderingLoads = 0;
}
